import { useEffect, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFirebaseAuth, getFirebaseDb } from '../config/firebase';

export type Counters = Record<string, number>;

export type LedgerRoot = {
  currentMonthKey?: string;
  currentMonthCounters?: Counters;
  lifetime?: Counters;
};

export type MonthRow = {
  monthKey: string;
  counters: Counters;
};

/**
 * Usage ledger reads (usageLedgers/{uid}) — shared by the home dashboard and
 * the Usage page. Root doc is live; months history is fetched once when
 * `withMonths` is set.
 */
export function useUsageLedger(withMonths = false): {
  root: LedgerRoot | null;
  months: MonthRow[] | null;
  ready: boolean;
  hasAnyUsage: boolean;
} {
  const [root, setRoot] = useState<LedgerRoot | null>(null);
  const [months, setMonths] = useState<MonthRow[] | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const uid = getFirebaseAuth().currentUser?.uid;
    if (!uid) return;
    const db = getFirebaseDb();
    const unsub = onSnapshot(doc(db, 'usageLedgers', uid), (snap) => {
      setRoot((snap.data() as LedgerRoot) || null);
      setReady(true);
    });
    if (withMonths) {
      void getDocs(
        query(collection(db, 'usageLedgers', uid, 'months'), orderBy('monthKey', 'desc')),
      ).then((snap) => {
        setMonths(
          snap.docs.map((d) => ({
            monthKey: (d.data().monthKey as string) || d.id,
            counters: (d.data().counters as Counters) || {},
          })),
        );
      });
    }
    return unsub;
  }, [withMonths]);

  const hasAnyUsage = Object.values(root?.lifetime || {}).some((v) => Number(v) > 0);

  return { root, months, ready, hasAnyUsage };
}
